import { useState } from 'react';
import useDebounce from '../../../../hooks/useDebounce/useDebounce';
import {
  usePrimeRachelChat,
  useRachelChatsList,
} from '../../../../services/modules/rachelChats/rachelChats.queries';
import { useRachelChatContext } from '../../contexts/Rachel.context';
import { useRachelActiveChatId } from '../../hooks/useRachelActiveChatId';
import { useRachelErrorReporter } from '../../hooks/useRachelErrorReporter';
import { flattenChatGroups, groupChatsByDay } from './rachelChatHistoryModal.utils';

const SEARCH_DEBOUNCE_IN_MS = 400;

export const useRachelChatHistoryModal = (onClose: () => void) => {
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, SEARCH_DEBOUNCE_IN_MS);
  const { query } = useRachelChatsList({ search: debouncedSearch || undefined });
  const activeChatId = useRachelActiveChatId();
  const { selectChat, startNewConversation } = useRachelChatContext();
  const { reportRachelError } = useRachelErrorReporter();
  const primeRachelChat = usePrimeRachelChat();

  const groups = groupChatsByDay(query.pagesData ?? []);
  const { groupCounts, chats: flatChats } = flattenChatGroups(groups);
  const isSearchPending = trimmedSearch !== debouncedSearch;
  const isRefetching = query.isFetching && !query.isLoading && !query.isFetchingNextPage;

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const handleSelect = async (chatId: string) => {
    if (chatId !== activeChatId) {
      primeRachelChat(flatChats.find((chat) => chat.id === chatId));
      try {
        await selectChat(chatId);
      } catch (error) {
        reportRachelError(error);
        return;
      }
    }
    handleClose();
  };

  const handleNewConversation = () => {
    startNewConversation();
    handleClose();
  };

  return {
    groups,
    groupCounts,
    flatChats,
    isLoading: query.isLoading,
    isSearching: isSearchPending || isRefetching,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    search,
    setSearch,
    hasSearch: Boolean(trimmedSearch),
    activeChatId,
    handleClose,
    handleSelect,
    handleNewConversation,
  };
};
